import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useMediaQuery, useTheme } from "@mui/material";

import logo from "../../../../assets/logo.png";
import BarekamButton from "../../../components/button/barekam-button";
import { ActionbarButtons, HeaderButtons } from "../../../components/button";
import BarekamText from "../../../components/text";
import { Message, MessageSections } from "../../../components/message/message";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import LanguageButton from "../../../header/header-buttons/language-button/language-button";
import UserButton from "../../../header/header-buttons/user-button/user-button";
import { getLanguages } from "../../../../redux/slices/static-data.slice";
import {
  HeaderContainer,
  Nav,
  NavItem,
  Logo,
  BurgerMenu,
  RightContainer,
  LeftContainer,
} from "./styled.components";
import { MessageLoader } from "../../../../router/blocks/route-components/message.loader";
import { changeDevice } from "../../../../redux/slices/device.slice";
import { navigateToSection } from "../../../../utils/smoothScroll";
import { useCalculatorModal } from "../../../../contexts/CalculatorModalContext";


const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const { language } = useParams();
  const [searchParams] = useSearchParams();
  const scrollTo = searchParams.get('scrollTo');
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAppSelector((state) => state.loginReducer);
  const { openCalculator } = useCalculatorModal();

  const matchesPc = useMediaQuery(theme.breakpoints.up(1500));
  const matchesTablet =
    useMediaQuery(theme.breakpoints.between(1200, 1500)) &&
    window.innerHeight > 500;
  const matchesPhone =
    useMediaQuery(theme.breakpoints.between(0, 1200)) ||
    window.innerHeight <= 500;

  useEffect(() => {
    Promise.allSettled([dispatch(getLanguages())]).then(() => setIsReady(true));
  }, []);

  useEffect(() => {
    dispatch(changeDevice({ matchesPc, matchesTablet, matchesPhone }));
  }, [matchesPhone, matchesTablet, matchesPc]);

  useEffect(() => {
    if (scrollTo) {
      navigateToSection(navigate, language || 'en', scrollTo, -700);
    }
  }, [scrollTo, navigate]);

  if (!isReady) return null;

  return (
    <HeaderContainer>
      <LeftContainer>
        <Logo
          onClick={() => {
            if (window.location.pathname === `/${language}/`) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate(`/${language}/`);
            }
          }}
          className={"logo"}
          src={logo}
          alt={""}
          style={{
            height: matchesPc || matchesTablet ? "3rem" : "2.5rem",
          }}
        />
        <MessageLoader sectionName={MessageSections.MAIN}>
          {!matchesPhone && (
            <BarekamButton
              type={ActionbarButtons.NEW}
              size={"l"}
              onClick={() => navigateToSection(navigate, language || 'en', 'track')}
            >
              <BarekamText variant={"m"} fontWeight={500} align={"center"}>
                <Message
                  messageKey={"track_order"}
                  sectionId={MessageSections.MAIN}
                />
              </BarekamText>
            </BarekamButton>
          )}
          <Nav isOpen={isMenuOpen} onClick={() => setMenuOpen(false)}>
            <NavItem onClick={() => navigateToSection(navigate, language || 'en', 'prohibited')}>
              <Message messageKey={"item_1"} sectionId={MessageSections.MAIN} />
            </NavItem>
            <NavItem onClick={() => navigateToSection(navigate, language || 'en', 'prices')}>
              <Message messageKey={"item_2"} sectionId={MessageSections.MAIN} />
            </NavItem>
            <NavItem onClick={openCalculator}>
              <Message messageKey={"calculate"} sectionId={MessageSections.MAIN} />
            </NavItem>
            <NavItem onClick={() => navigateToSection(navigate, language || 'en', 'wholesale')}>
              <Message messageKey={"item_3"} sectionId={MessageSections.MAIN} />
            </NavItem>
            <NavItem onClick={() => navigate(`/${language}/about-us`)}>
              <Message messageKey={"item_4"} sectionId={MessageSections.MAIN} />
            </NavItem>
            <NavItem onClick={() => navigateToSection(navigate, language || 'en', 'contact')}>
              <Message messageKey={"item_5"} sectionId={MessageSections.MAIN} />
            </NavItem>
          </Nav>
        </MessageLoader>
      </LeftContainer>
      <RightContainer>
        {isLoggedIn ? (
          <>
            {!matchesPhone && (
              <BarekamButton
                type={ActionbarButtons.NEW}
                size={"l"}
                onClick={() => navigate(`/${language}/orders/my`)}
              >
                <BarekamText variant={"m"} fontWeight={500} align={"center"}>
                  <Message
                    messageKey={"my_orders"}
                    sectionId={MessageSections.MAIN}
                  />
                </BarekamText>
              </BarekamButton>
            )}
            <UserButton
              isAdmin={false}
              isWarehouseWorker={false}
              hasValidatedRecipient={true}
              isDesktop={matchesPc}
              isTablet={matchesTablet}
              isPdr={false}
              key={"user-button"}
            />
          </>
        ) : (
          <MessageLoader sectionName={MessageSections.LOGIN}>
            <BarekamButton
              type={HeaderButtons.HEADER_MY_ADDRESSES}
              size={"l"}
              onClick={() => navigate(`/${language}/login`)}
            >
              <BarekamText variant={"m"} fontWeight={500} align={"center"}>
                <Message
                  messageKey={"sign_in"}
                  sectionId={MessageSections.LOGIN}
                />
              </BarekamText>
            </BarekamButton>
            {!matchesPhone && (
              <BarekamButton
                type={ActionbarButtons.NEW}
                size={"l"}
                onClick={() => navigate(`/${language}/register`)}
              >
                <BarekamText variant={"m"} fontWeight={500} align={"center"}>
                  <Message
                    messageKey={"create_account"}
                    sectionId={MessageSections.LOGIN}
                  />
                </BarekamText>
              </BarekamButton>
            )}
          </MessageLoader>
        )}
        <MessageLoader sectionName={MessageSections.MAIN}>
          <LanguageButton
            isDesktop={matchesPc}
            key={"language-button"}
            newHeader
          />
        </MessageLoader>
        <BurgerMenu onClick={() => setMenuOpen(!isMenuOpen)}>☰</BurgerMenu>
      </RightContainer>
    </HeaderContainer>
  );
};

export default Header;
